import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const RECAPTCHA_SITE_KEY = "6LcJfwQsAAAAAK_sTgE4Z_4Es4QOAxV1eqqAjyFo";

function ContactSection() {
  const [agree, setAgree] = useState(false);
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    company: '',
    name: '',
    nameKana: '',
    tel: '',
    email: '',
    message: '',
    honeypot: '' //  HoneyPot フィールド追加
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState('');
  const [siteKey, setSiteKey] = useState('');

  // --- reCAPTCHA キーを WordPress から取得 ---
  useEffect(() => {
    const fetchSiteKey = async () => {
      try {
        const res = await axios.get('https://chaln.co.jp/wp-json/wp/v2/recaptcha-key');
        if (res.data.success && res.data.site_key) {
          setSiteKey(res.data.site_key);
        } else {
          console.error('Site key fetch error:', res.data);
          setStatus('reCAPTCHAキーの取得に失敗しました。');
        }
      } catch (err) {
        console.error('Site key fetch error:', err);
        setStatus('reCAPTCHAキーの取得に失敗しました。');
      }
    };
    fetchSiteKey();
  }, []);

  //  reCAPTCHAスクリプトの重複読み込み防止
  useEffect(() => {
    if (!document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]')) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit&onload=onloadCallback";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // reCAPTCHA v2 のウィジェット描画
  useEffect(() => {
    // Google API の onload で呼ばれるグローバル関数を定義
    window.onloadCallback = function () {
      if (window.grecaptcha && siteKey) {
        window.grecaptcha.render("recaptcha-container", {
          sitekey: siteKey,
        });
      }
    };

    // siteKey が取得済みで、script も読み込まれている場合に即実行
    if (window.grecaptcha && siteKey) {
      window.grecaptcha.render("recaptcha-container", {
        sitekey: siteKey,
      });
    }
  }, [siteKey]);

  useEffect(() => {
    const fetchNonce = async () => {
      try {
        const response = await axios.get('https://chaln.co.jp/wp-json/wp/v2/get-nonce');
        if (response.data.success) {
          setNonce(response.data.nonce);
        } else {
          setStatus('セキュリティトークンの取得に失敗しました');
        }
      } catch (error) {
        console.error('Nonce fetch error:', error);
        setStatus('セキュリティトークンの取得に失敗しました');
      }
    };

    fetchNonce();
  }, []);

  const handlePrivacyPolicyClick = () => {
    navigate('/privacy-policy');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {

    //  nonce確認
    if (!nonce) {
      setStatus('セキュリティトークンが無効です。ページを再読み込みしてください。');
      return false;
    }

    //  HoneyPot チェック（ボットが入力した場合は拒否）
    if (formData.honeypot) {
      setStatus('不正な送信が検出されました。');
      return false;
    }

    if (
      !formData.company ||
      !formData.name ||
      !formData.nameKana ||
      !formData.email ||
      !formData.tel ||
      !formData.message
    ) {
      setStatus('必須項目を入力してください。');
      return false;
    }
    if (!formData.email.includes('@')) {
      setStatus('正しいメールアドレスを入力してください。');
      return false;
    }
    if (!agree) {
      setStatus('個人情報保護方針に同意してください。');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    if (!validateForm()) return;

    setLoading(true);

    try {
      const recaptchaResponse = document.querySelector('.g-recaptcha-response')?.value;
      if (!recaptchaResponse) {
        setStatus('reCAPTCHAを完了してください。');
        setLoading(false);
        return;
      }
      const response = await axios.post('https://chaln.co.jp/wp-json/wp/v2/contact', {
        ...formData,
        recaptcha_response: recaptchaResponse,
        nonce: nonce
      }, {
        headers: { 'Content-Type': 'application/json' }
      });


      if (response.data.success) {
        setStatus('メールが送信されました。');
        setFormData({
          company: '',
          name: '',
          nameKana: '',
          tel: '',
          email: '',
          message: '',
          honeypot: '' //  HoneyPot もリセット
        });
        setAgree(false);
        try {
          const nonceResponse = await axios.get('https://chaln.co.jp/wp-json/wp/v2/get-nonce');
          if (nonceResponse.data.success) {
            setNonce(nonceResponse.data.nonce);
          }
        } catch (error) {
          console.error('Nonce refresh error:', error);
        }
      } else {
        setStatus(response.data.message || '送信に失敗しました。');
      }
    } catch (err) {
      console.error('Error:', err.response?.data);
      setStatus(err.response?.data?.message || '送信中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let hasAppeared = false;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasAppeared) {
            setShouldAnimate(true);
            hasAppeared = true;
          }
          setVisible(true);
        } else {
          setVisible(false);
          hasAppeared = false;
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contact"
      ref={ref}
      className={
        `rounded-3xl p-6 md:p-12 shadow-2xl backdrop-blur-md border border-cyan-200/10 hover:border-cyan-300/30 mb-16 max-w-5xl mx-auto ` +
        (visible ? 'opacity-100' : 'opacity-0')
      }
      style={{
        willChange: 'opacity',
        transition: shouldAnimate
          ? 'opacity 1.2s cubic-bezier(.4,0,.2,1)'
          : 'none'
      }}
      onTransitionEnd={() => setShouldAnimate(false)}
    >
      <h2 className="text-3xl md:text-5xl font-black tracking-tight drop-shadow-xl text-white mb-6 border-b border-orange-600/20 pb-2 ">
        Contact
      </h2>

      <form className="max-w-2xl mx-auto space-y-6" onSubmit={handleSubmit}>
        {/*  HoneyPot フィールド（非表示） */}
        <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
          <label htmlFor="honeypot">サイトURL（入力しないでください）</label>
          <input
            type="text"
            id="honeypot"
            name="honeypot"
            value={formData.honeypot}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* 会社名 */}
        <div>
          <label htmlFor="company" className="block text-orange-400 font-semibold mb-1">
            会社名
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <input
            type="text"
            id="company"
            name="company"
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition"
            autoComplete="organization"
            value={formData.company}
            onChange={handleChange}
          />
        </div>

        {/* 氏名 */}
        <div>
          <label htmlFor="name" className="block text-orange-400 font-semibold mb-1">
            氏名
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition"
            autoComplete="name"
            value={formData.name}
            onChange={handleChange}
          />
        </div>

        {/* 氏名（フリガナ） */}
        <div>
          <label htmlFor="nameKana" className="block text-orange-400 font-semibold mb-1">
            氏名（フリガナ）
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <input
            type="text"
            id="nameKana"
            name="nameKana"
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition"
            autoComplete="off"
            value={formData.nameKana}
            onChange={handleChange}
          />
        </div>

        {/* 電話番号 */}
        <div>
          <label htmlFor="tel" className="block text-orange-400 font-semibold mb-1">
            電話番号
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <input
            type="tel"
            id="tel"
            name="tel"
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition"
            autoComplete="tel"
            value={formData.tel}
            onChange={handleChange}
          />
        </div>

        {/* メールアドレス */}
        <div>
          <label htmlFor="email" className="block text-orange-400 font-semibold mb-1">
            メールアドレス
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        {/* お問い合わせ内容 */}
        <div>
          <label htmlFor="message" className="block text-orange-400 font-semibold mb-1">
            お問い合わせ内容
            <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-600 rounded align-middle">必須</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            className="w-full rounded-lg border border-orange-600/20 bg-gray-500/30 text-white p-3 focus:outline-none focus:ring-2 focus:ring-orange-600 transition resize-vertical min-h-[120px]"
            value={formData.message}
            onChange={handleChange}
          />
        </div>

        {/* プライバシーポリシー同意 */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="agree"
            name="agree"
            checked={agree}
            onChange={() => setAgree(!agree)}
            className="form-checkbox h-5 w-5 text-orange-600 bg-gray-500 border-orange-600/40 rounded focus:ring-orange-600 transition"
          />
          <label htmlFor="agree" className="ml-2 text-white text-sm select-none">
            <span
              onClick={handlePrivacyPolicyClick}
              className="text-orange-400 hover:text-orange-300 underline cursor-pointer transition"
            >
              問い合わせにおける個人情報保護方針
            </span>について同意する
          </label>
        </div>
        {/* reCAPTCHA */}
        <div id="recaptcha-container"></div>

        {status && <p className="text-red-400 text-center">{status}</p>}

        {/* 送信ボタン */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full py-3 rounded-full bg-gradient-to-r from-orange-600 via-blue-500 to-purple-500 text-white font-bold text-lg shadow-lg hover:scale-105 transition disabled:opacity-60"
            disabled={loading || !nonce}
          >
            {loading ? '送信中...' : 'この内容で送信する'}
          </button>
        </div>

        {/*  Google公式推奨の文言（規約遵守用） */}
        <p className="text-xs text-gray-500 text-center mt-4">
          このサイトは reCAPTCHA によって保護されています。
          <br />
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-gray-700"
          >
            Google プライバシーポリシー
          </a>
          と
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-gray-700"
          >
            利用規約
          </a>
          が適用されます。
        </p>
      </form>
    </section>
  );
}

export default ContactSection;
